import { Editor } from '@tiptap/core'
import { DOMSerializer } from '@tiptap/pm/model'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'

/**
 * Alpine.js component: tiptapEditor
 *
 * Usage in Blade:
 *   <div x-data="tiptapEditor($wire, 'editorialContent')" x-ref="editorEl"></div>
 *
 * @param {object} wire    — Livewire $wire proxy
 * @param {string} prop    — Livewire property name to sync
 */
document.addEventListener('alpine:init', () => {
    Alpine.data('tiptapEditor', (wire, prop) => ({
        editor: null,
        syncing: false,
        isEditing: false,

        init() {
            const content = wire.get(prop) || ''

            this.editor = new Editor({
                element: this.$refs.editorEl,
                extensions: [
                    StarterKit.configure({
                        heading: { levels: [1, 2, 3, 4] },
                    }),
                    Link.configure({
                        openOnClick: false,
                        HTMLAttributes: { rel: 'noopener noreferrer nofollow' },
                    }),
                    Image.configure({
                        HTMLAttributes: { class: 'rounded-md shadow-md mx-auto' },
                    }),
                    Placeholder.configure({
                        placeholder: 'Écrivez votre contenu ici…',
                    }),
                    Underline,
                    TextAlign.configure({
                        types: ['heading', 'paragraph'],
                    }),
                ],
                content: content,
                editorProps: {
                    attributes: {
                        class: 'prose prose-sm dark:prose-invert max-w-none min-h-[300px] px-6 py-4 focus:outline-none ' +
                               'prose-headings:text-heading ' +
                               'prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3 prose-h2:border-b prose-h2:border-edge prose-h2:pb-2 ' +
                               'prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-2 ' +
                               'prose-p:text-body prose-p:leading-relaxed ' +
                               'prose-li:text-body ' +
                               'prose-strong:text-heading ' +
                               'prose-a:text-primary-600 dark:prose-a:text-primary-400 ' +
                               'prose-img:rounded-md prose-img:shadow-md prose-img:mx-auto',
                    },
                },
                onUpdate: ({ editor }) => {
                    if (!this.syncing) {
                        this.isEditing = true
                        // Debounce sync to Livewire
                        clearTimeout(this._debounce)
                        this._debounce = setTimeout(() => {
                            wire.set(prop, editor.getHTML())
                        }, 500)
                    }
                },
            })

            // Listen for external content updates (e.g. after AI rewrite)
            this.$watch(() => wire.get(prop), (newVal) => {
                if (newVal !== this.editor.getHTML()) {
                    this.syncing = true
                    this.editor.commands.setContent(newVal || '', false)
                    this.syncing = false
                }
            })
        },

        destroy() {
            this.editor?.destroy()
        },

        // Toolbar actions
        toggleBold()      { this.editor.chain().focus().toggleBold().run() },
        toggleItalic()    { this.editor.chain().focus().toggleItalic().run() },
        toggleUnderline() { this.editor.chain().focus().toggleUnderline().run() },
        toggleStrike()    { this.editor.chain().focus().toggleStrike().run() },
        toggleH2()        { this.editor.chain().focus().toggleHeading({ level: 2 }).run() },
        toggleH3()        { this.editor.chain().focus().toggleHeading({ level: 3 }).run() },
        toggleH4()        { this.editor.chain().focus().toggleHeading({ level: 4 }).run() },
        toggleBullet()    { this.editor.chain().focus().toggleBulletList().run() },
        toggleOrdered()   { this.editor.chain().focus().toggleOrderedList().run() },
        toggleBlockquote(){ this.editor.chain().focus().toggleBlockquote().run() },
        setHr()           { this.editor.chain().focus().setHorizontalRule().run() },
        undo()            { this.editor.chain().focus().undo().run() },
        redo()            { this.editor.chain().focus().redo().run() },
        clearFormat()     { this.editor.chain().focus().clearNodes().unsetAllMarks().run() },

        setLink() {
            const url = prompt('URL du lien :')
            if (url) {
                this.editor.chain().focus().setLink({ href: url }).run()
            }
        },

        unsetLink() {
            this.editor.chain().focus().unsetLink().run()
        },

        insertImage() {
            const url = prompt('URL de l\'image :')
            if (url) {
                this.editor.chain().focus().setImage({ src: url }).run()
            }
        },

        // Check active state for toolbar button highlighting
        isActive(type, attrs = {}) {
            return this.editor?.isActive(type, attrs) ?? false
        },

        // Get selected HTML for AI rewrite
        getSelectedHtml() {
            const { from, to } = this.editor.state.selection
            if (from === to) return ''
            const slice = this.editor.state.doc.slice(from, to)
            const serializer = DOMSerializer.fromSchema(this.editor.schema)
            const tempDiv = document.createElement('div')
            tempDiv.appendChild(serializer.serializeFragment(slice.content))
            return tempDiv.innerHTML
        },

        getSelectedText() {
            const { from, to } = this.editor.state.selection
            if (from === to) return ''
            return this.editor.state.doc.textBetween(from, to, '\n')
        },

        hasSelection() {
            const { from, to } = this.editor.state.selection
            return from !== to
        },

        replaceSelection(html) {
            this.editor.chain().focus().deleteSelection().insertContent(html).run()
        },
    }))
})
